import { SMTP_PRESETS, SMTP_PRESET_CUSTOM } from './constants'

// A preset stays "selected" purely because the host still matches it, so an
// edit to the host field silently moves the choice back to Other.
export const findSmtpPreset = host =>
  SMTP_PRESETS.find(preset => preset.host === (host || '').trim().toLowerCase())

// A free consumer address names its own mail host, so the provider question can
// answer itself. A custom domain cannot be resolved without a DNS lookup, so it
// deliberately returns nothing and leaves the choice to the user.
export const findSmtpPresetByEmail = email => {
  const domain = (email || '').trim().toLowerCase().split('@')[1]
  if (!domain) return undefined
  return SMTP_PRESETS.find(preset => preset.mailboxDomains.includes(domain))
}

// Users paste whatever their provider's documentation shows: a URL, a host:port
// pair, or a full endpoint. Reduce that to the bare hostname the backend expects
// and hand back any port that came along so the Port select can follow it.
export const parseSmtpHostInput = raw => {
  let value = (raw || '').trim()
  if (!value) return { host: '', port: null }

  const withoutScheme = value.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
  const hadScheme = withoutScheme !== value
  value = withoutScheme.split('/')[0]

  // Only a URL's credentials are dropped. A bare "noreply@example.com" keeps its
  // "@" so validation can call it what it is: an email address, not a host.
  if (hadScheme) value = value.split('@').pop()

  let port = null
  const portMatch = value.match(/:(\d+)$/)
  if (portMatch) {
    port = parseInt(portMatch[1])
    value = value.slice(0, portMatch.index)
  }

  return { host: value.toLowerCase(), port }
}

const HOSTNAME_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i
const IPV4_PATTERN = /^(\d{1,3}\.){3}\d{1,3}$/

// Not a strict RFC check: it only has to catch what this field actually receives
// by mistake: an email address, a URL, or a bare word with no domain at all.
export const isValidSmtpHost = host => {
  const value = (host || '').trim()
  if (!value) return false
  return IPV4_PATTERN.test(value) || HOSTNAME_PATTERN.test(value)
}

// The domain half of an address is the part that says which provider a
// collection sends through; the mailbox half stays in the browser.
export const emailDomain = address =>
  (address || '').trim().toLowerCase().split('@')[1] || null

// Which relay was configured, as a name rather than a hostname: the two presets
// we offer, or "custom" for anything the user typed themselves.
export const smtpPresetId = host => {
  const value = (host || '').trim()
  if (!value) return null
  return findSmtpPreset(value)?.id || SMTP_PRESET_CUSTOM
}

const ERROR_MESSAGE_LIMIT = 120

// API failures arrive as BaseError, not Error, so there is no message to read.
// What is readable is a 400's detail string, or the body of the response.
export const describeApiError = error => {
  const response = error?.response
  const detail =
    typeof response === 'string'
      ? response
      : response?.data?.detail || response?.data?.title || null

  return {
    error_type: error?.type || 'UnknownError',
    error_status: typeof response === 'object' ? response?.status ?? null : null,
    error_message: detail ? detail.slice(0, ERROR_MESSAGE_LIMIT) : null,
  }
}
